/**
 * RecordingBar — premium in-input voice recording UI.
 *
 * Replaces the chat input + send button while voice recording is active.
 * Shows: ✕ cancel · "Listening…" + live audio bars + mm:ss · ✓ confirm.
 * During upload it morphs into a compact "Transcribing…" state.
 *
 * Driven by `useVoiceRecorder` — pass the values through.
 */
import { Check, X, Loader2 } from 'lucide-react';

function formatTime(s) {
  const mm = String(Math.floor(s / 60)).padStart(2, '0');
  const ss = String(Math.floor(s % 60)).padStart(2, '0');
  return `${mm}:${ss}`;
}

export default function RecordingBar({
  phase = 'recording',          // 'recording' | 'uploading'
  levels = [],
  seconds = 0,
  maxSeconds = 300,
  onConfirm,
  onCancel,
  testIdPrefix = 'voice',
}) {
  const isUploading = phase === 'uploading';
  const remaining = Math.max(0, maxSeconds - seconds);
  const nearLimit = remaining <= 15 && !isUploading;

  return (
    <div
      data-testid={`${testIdPrefix}-recording-bar`}
      className="flex items-center gap-2 sm:gap-3 w-full px-1.5 py-1 select-none"
    >
      {/* Cancel */}
      <button
        type="button"
        onClick={onCancel}
        disabled={isUploading}
        data-testid={`${testIdPrefix}-cancel-btn`}
        title="Cancel recording"
        aria-label="Cancel recording"
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-[0.94]
          ${isUploading
            ? 'bg-gray-900/[0.04] text-gray-300 cursor-not-allowed'
            : 'bg-gray-900/[0.05] text-gray-500 hover:text-red-500 hover:bg-red-500/10'}`}
      >
        <X className="w-[15px] h-[15px]" />
      </button>

      {/* Center: Listening + waveform + timer */}
      <div className="flex-1 flex items-center justify-center gap-2.5 sm:gap-3 min-w-0 px-1">
        {isUploading ? (
          <div className="flex items-center gap-2 text-gray-500">
            <Loader2 className="w-3.5 h-3.5 animate-spin text-emerald-500" />
            <span className="text-[12px] font-medium tracking-wide">Transcribing…</span>
          </div>
        ) : (
          <>
            <span
              data-testid={`${testIdPrefix}-listening-label`}
              className="hidden sm:inline text-[11px] font-medium uppercase tracking-[0.14em] text-emerald-600/90"
            >
              Listening<span className="opacity-70">…</span>
            </span>
            <Waveform levels={levels} />
            <span
              data-testid={`${testIdPrefix}-timer`}
              className={`text-[11px] tabular-nums font-medium select-none transition-colors ${
                nearLimit ? 'text-amber-500' : 'text-gray-500'
              }`}
            >
              {formatTime(seconds)}
            </span>
          </>
        )}
      </div>

      {/* Confirm */}
      <button
        type="button"
        onClick={onConfirm}
        disabled={isUploading}
        data-testid={`${testIdPrefix}-confirm-btn`}
        title="Confirm and transcribe"
        aria-label="Confirm and transcribe"
        className={`shrink-0 w-9 h-9 rounded-full flex items-center justify-center transition-all duration-150 active:scale-[0.94]
          ${isUploading
            ? 'bg-emerald-500/40 text-white/80 cursor-not-allowed'
            : 'bg-emerald-500 text-white hover:bg-emerald-600 shadow-[0_4px_14px_rgba(16,185,129,0.35)]'}`}
      >
        {isUploading
          ? <Loader2 className="w-[15px] h-[15px] animate-spin" />
          : <Check className="w-[15px] h-[15px]" />}
      </button>
    </div>
  );
}

function Waveform({ levels }) {
  const bars = levels && levels.length ? levels : new Array(28).fill(0);
  return (
    <div
      className="flex items-center gap-[3px] h-7 sm:h-8"
      role="img"
      aria-label="Live audio waveform"
    >
      {bars.map((v, i) => {
        // Idle floor: subtle breathing animation around 14% so bars never look "dead".
        const live = Math.max(v, 0);
        // Add a tiny shape to make the wave feel organic (taller in the middle).
        const center = 1 - Math.abs(i - bars.length / 2) / (bars.length / 2);
        const shaped = Math.min(1, live * (0.55 + 0.65 * center));
        const heightPct = Math.max(12, shaped * 100);
        const isActive = live > 0.06;
        return (
          <span
            key={i}
            className={`w-[3px] rounded-full transition-[height] duration-[80ms] ease-out ${
              isActive ? 'bg-emerald-500' : 'bg-emerald-400/35'
            }`}
            style={{
              height: `${heightPct}%`,
            }}
          />
        );
      })}
    </div>
  );
}
